import React, { useEffect, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import PublishIcon from "@mui/icons-material/Publish";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import { ClinicImages } from "./components/ClinicImages";
import { ClinicInfo } from "./components/ClinicInfo";
import { AboutUs } from "./components/AboutUs";
import { Treatments } from "./components/Treatments";
import { Doctors } from "./components/Doctors";
import { setClinicData } from "../../../redux/bookclinic/bookclinicaction";
import baseUrl from "../../../api/baseUrl";
import {
  IClinicImage,
  IClinicInfo,
  ITreatment,
  ISubTreatment,
  IDoctor,
} from "./types/clinic.types";

function normalizeBookTreatment(raw: any): ITreatment {
  const subs: ISubTreatment[] = (raw.subTreatments || raw.subTreatment || []).map(
    (s: any) => ({
      id: String(s.id ?? s._id ?? ""),
      subTreatment: s.subTreatment ?? "",
      description: s.description ?? "",
      localPrice: Number(s.localPrice ?? s.price ?? 0),
      foreignerPrice: Number(s.foreignerPrice ?? s.foreginPrice ?? 0),
      minlocalPrice: Number(s.minlocalPrice ?? s.minPrice ?? 0),
      maxlocalPrice: Number(s.maxlocalPrice ?? s.maxPrice ?? 0),
      minforeignerPrice: Number(
        s.minforeignerPrice ?? s.minForeginPrice ?? 0
      ),
      maxforeignerPrice: Number(
        s.maxforeignerPrice ?? s.maxForeginPrice ?? 0
      ),
      duration: Number(s.duration ?? 0),
      note: s.note ?? "",
    })
  );
  const tid = raw?.treatmentId;
  const treatmentId =
    tid == null
      ? undefined
      : typeof tid === "object" && tid !== null && "_id" in tid
        ? String((tid as { _id: unknown })._id)
        : String(tid);
  return {
    ...raw,
    id: String(raw.id ?? raw._id ?? ""),
    treatmentId,
    subTreatments: subs,
  };
}
const ClinicManagementPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(true);
  const [images, setImages] = useState<IClinicImage[]>([]);
  const [info, setInfo] = useState<IClinicInfo | null>(null);
  const [about, setAbout] = useState<string>("");
  const [treatments, setTreatments] = useState<ITreatment[]>([]);
  const [doctors, setDoctors] = useState<IDoctor[]>([]);
  const [status, setStatus] = useState<"draft" | "published">("draft");
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDrafting, setIsDrafting] = useState(false);

  useEffect(() => {
    baseUrl
      .get("/client/booking/findBookClinicId")
      .then((response) => {
        const clinicData = response?.data?.data;

        if (clinicData) {
          // Normalize images to ensure they have imageUrl property
          const normalizedImages = (clinicData.images || []).map((img: any) => ({
            ...img,
            imageUrl: img.imageUrl || img.url || img.image || "",
            url: img.url || img.imageUrl || img.image || "",
            id: img.id || img._id || `img_${Date.now()}_${Math.random()}`,
          }));

          // Set all your states from the API response
          setImages(normalizedImages);
          setInfo({
            ...clinicData,
            location: clinicData.location || {
              addressLine: "",
              street: "",
              district: "",
              city: "",
              map: "",
            },
          });
          setAbout(clinicData.about || "");
          setTreatments(
            (clinicData.bookTreatment || []).map(normalizeBookTreatment)
          );
          setStatus(clinicData.status || "draft");
          setClinicData(clinicData);
        } else {
          // If no data from API, set empty defaults
          setImages([]);
          setInfo(null);
          setAbout("");
          setTreatments([]);
          setStatus("draft");
        }
      })
      .catch((error) => {
        console.error("Error loading clinic data:", error);
        // Set empty defaults on error
        setImages([]);
        setInfo(null);
        setAbout("");
        setTreatments([]);
        setStatus("draft");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    baseUrl
      .get("/client/booking/findEmployee", {
        headers: { status: true },
      })
      .then((response) => {
        const clinicData = response?.data?.data;
        setDoctors(clinicData || []);
      })
      .catch((error) => {
        console.error("Error loading doctors:", error);
        setDoctors([]);
      })
      .finally(() => {
        setIsLoadingDoctors(false);
      });
  }, []);
  // --- Handlers to update state (passed to children) ---
  const handleImagesChange = (newImages: IClinicImage[]) => {
    setImages(newImages);
  };

  const handleInfoChange = (newInfo: IClinicInfo) => {
    setInfo(newInfo);
  };

  const handleAboutChange = (newAbout: string) => {
    setAbout(newAbout);
  };

  const handleTreatmentsChange = (newTreatments: ITreatment[]) => {
    setTreatments(newTreatments);
  };

  const handleDoctorsChange = (newDoctors: IDoctor[]) => {
    setDoctors(newDoctors);
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const response = await baseUrl.patch("/client/booking/statusBookClinic", {
        status: "published",
      });

      if (response.data?.success) {
        setStatus("published");
        toast.success("Clinic published successfully!");
      } else {
        toast.error("Failed to publish clinic");
      }
    } catch (error: any) {
      console.error("Error publishing clinic:", error);
      toast.error(error?.response?.data?.message || "Failed to publish clinic");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDraft = async () => {
    setIsDrafting(true);
    try {
      const response = await baseUrl.patch("/client/booking/statusBookClinic", {
        status: "draft",
      });

      if (response.data?.success) {
        setStatus("draft");
        toast.success("Clinic moved to draft successfully!");
      } else {
        toast.error("Failed to move clinic to draft");
      }
    } catch (error: any) {
      console.error("Error moving clinic to draft:", error);
      toast.error(error?.response?.data?.message || "Failed to move clinic to draft");
    } finally {
      setIsDrafting(false);
    }
  };

  const isPublished = status === "published";
  if (isLoading || isLoadingDoctors) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center bg-slate-50 px-4">
        <div className="max-w-sm text-center">
          <div
            className="mx-auto mb-4 h-11 w-11 animate-spin rounded-full border-2 border-primary border-t-transparent"
            role="status"
            aria-label="Loading"
          />
          <h2 className="text-base font-semibold text-slate-800">Loading clinic profile</h2>
          <p className="mt-2 text-sm text-slate-500">Fetching images, services, and team…</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-full w-full min-w-0 bg-gradient-to-b from-slate-50 to-white">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8 lg:max-w-6xl lg:px-8">
        <header className="mb-6 min-w-0 sm:mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Branding</p>
          <h1 className="mt-1 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
            Clinic booking profile
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-[0.9375rem]">
            Update each section below and use <strong className="font-semibold text-slate-700">Save</strong> inside
            the cards. When you are ready, publish so patients can book online.
          </p>
        </header>

        <div className="flex min-w-0 flex-col gap-5 sm:gap-6 lg:gap-8" role="main">
          <ClinicImages images={images} onImagesChange={handleImagesChange} />
          <ClinicInfo info={info} onInfoChange={handleInfoChange} />
          <AboutUs aboutContent={about} onAboutChange={handleAboutChange} />
          <Treatments treatments={treatments} onTreatmentsChange={handleTreatmentsChange} />
          <Doctors doctors={doctors} treatments={treatments} onDoctorsChange={handleDoctorsChange} />
        </div>

        <section
          className="mt-8 rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm sm:mt-10 sm:p-5"
          aria-label="Publish status"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <div className="min-w-0 space-y-1 text-center sm:text-left">
              {isPublished ? (
                <div className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 sm:w-auto sm:justify-start">
                  <CheckCircleIcon sx={{ fontSize: 18 }} className="text-emerald-700 shrink-0" />
                  <div className="text-left">
                    <span className="text-sm font-semibold text-emerald-900">Live on booking</span>
                    <p className="text-xs text-emerald-800/90">Patients can see this clinic on the public site.</p>
                  </div>
                </div>
              ) : (
                <div className="w-full rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center sm:w-auto sm:text-left">
                  <span className="text-sm font-semibold text-amber-900">Draft</span>
                  <p className="mt-1 text-xs leading-snug text-amber-800/90">
                    Not visible to the public until you publish.
                  </p>
                </div>
              )}
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:shrink-0">
              {isPublished ? (
                <Button
                  variant="outlined"
                  onClick={handleDraft}
                  disabled={isDrafting}
                  fullWidth
                  startIcon={
                    isDrafting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <EditIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  sx={{
                    borderColor: "#f59e0b",
                    color: "#d97706",
                    "&:hover": {
                      borderColor: "#d97706",
                      backgroundColor: "rgba(245, 158, 11, 0.08)",
                    },
                    "&:disabled": {
                      borderColor: "#cbd5e1",
                      color: "#94a3b8",
                    },
                    "@media (min-width: 640px)": {
                      minWidth: "200px",
                      width: "auto",
                    },
                    py: 1.25,
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "12px",
                  }}
                >
                  {isDrafting ? "Moving to draft…" : "Unpublish (draft)"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handlePublish}
                  disabled={isPublishing}
                  fullWidth
                  startIcon={
                    isPublishing ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PublishIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  sx={{
                    background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
                    },
                    "&:disabled": {
                      background: "#94a3b8",
                      color: "#fff",
                    },
                    "@media (min-width: 640px)": {
                      minWidth: "220px",
                      width: "auto",
                    },
                    py: 1.25,
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "12px",
                    boxShadow: "0 4px 14px rgba(79, 70, 229, 0.35)",
                  }}
                >
                  {isPublishing ? "Publishing…" : "Publish clinic"}
                </Button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
export default ClinicManagementPage;